import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import moment from 'moment'
import { saveAs } from 'file-saver'
import { groupBy, keys, noop } from 'lodash'
import Modal from '@ublique/components/LoadingModal'
import Button from '@ublique/components/Button'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { selectModels } from '../selectors'

const MAX_ROWS = 5

const ModalDeletionError = ({ close, open, data }) => {
  const { deletionErrors, tryAgain = noop } = data
  const grouped = groupBy(deletionErrors, 'type')

  const models = useStoreSelector('appConfig', selectModels)
  const { t } = useTranslation('Document')
  const { t: modelsT } = useTranslation('Models')

  const translateDocumentTitle = useCallback(
    (document) => {
      return modelsT(`DocumentModel-${document}.${models[document]?.title}`)
    },
    [modelsT, document, models]
  )
  const translateDocumentDetailTitle = useCallback(
    (parent, detailId) => {
      const modelParent = models[parent]
      const detailTitle = modelParent.sections.find(
        (field) => field.id && field.id.toLowerCase() === `${detailId}_section`
      )?.title

      return modelsT(`DocumentModel-${parent}.${detailTitle}`)
    },
    [modelsT, document, models]
  )

  const downloadLogs = useCallback(() => {
    const reduceDocument = (acc, { name, type, detail }) =>
      `${acc}${name}${detail ? `${getDetail(type, detail)}` : ''}\n`

    // Create a text listed of various products
    const text = keys(grouped).reduce(
      (acc, document) =>
        `${acc}\n${translateDocumentTitle(document)} \n${grouped[
          document
        ].reduce(reduceDocument, '')}`,
      ''
    )

    const blobText = new Blob([text], { type: 'text/plain' })
    const fileName = `Deletion_error_${moment().format('YYYY-MM-DD_HH-mm')}.txt`

    saveAs(blobText, fileName)
  }, [grouped, translateDocumentTitle, getDetail, document])

  const tryAgainFunc = useCallback(() => {
    tryAgain()
    close()
  })

  const getDetail = (document, detail) => {
    const detailId = detail.id
    const detailTitle = translateDocumentDetailTitle(
      document,
      `${detail.type}s`
    )

    return ` (${t('DETAIL')}: ${detailTitle} - ID: ${detailId})`
  }

  const modalBody = () => {
    return (
      <>
        <h5>{t('CannotDeleteMessage')}</h5>
        <div className='modal-body-response'>
          {keys(grouped).map((document) => (
            <div key={document}>
              <p>{translateDocumentTitle(document)}</p>
              {grouped[document].slice(0, MAX_ROWS).map(({ name, detail }) => (
                <p className='name-error' key={name}>
                  {name}
                  {detail && (
                    <span className='name-error'>
                      {getDetail(document, detail)}
                    </span>
                  )}
                </p>
              ))}
              {grouped[document].length > MAX_ROWS && (
                <p className='name-error'>
                  (
                  {t('OtherEntries', {
                    count: grouped[document].length - MAX_ROWS
                  })}
                  )
                </p>
              )}
            </div>
          ))}
        </div>
      </>
    )
  }

  return (
    <Modal
      id='modal-deletion-error'
      preventCloseOnClickOutside
      modalHeading={t('CannotDeleteTitle')}
      open={open}
      onRequestClose={close}
      onSecondarySubmit={close}
      footerCustom={
        <>
          <Button kind='secondary' onClick={close}>
            {t('close')}
          </Button>
          <Button
            disabled={deletionErrors.length <= MAX_ROWS}
            kind='primary'
            onClick={downloadLogs}
          >
            {t('DownloadErrorLogs')}
          </Button>
          <Button kind='primary' onClick={tryAgainFunc}>
            {t('TryAgain')}
          </Button>
        </>
      }
    >
      {modalBody()}
    </Modal>
  )
}

export default ModalDeletionError
